import React, { useState, useEffect } from 'react';
import AdminMetrics from './AdminMetrics';
import AdminNotifications from './AdminNotifications';
import DatabaseStatus from './DatabaseStatus';
import { LoadingSpinner } from '../Loading/LoadingSpinner';
import AdminKeyboardShortcuts from './AdminKeyboardShortcuts';
import CopyButton from './CopyButton';

interface DashboardStats {
  total_registrations: number;
  today_registrations: number;
  emails_sent: number;
  emails_pending: number;
  recent_registrations: Array<{
    id: string;
    name: string;
    email: string;
    wallet_address: string;
    created_at: string;
    email_sent: boolean;
  }>;
}

interface AdminUser {
  id: number;
  username: string;
  email: string;
  role: string;
  lastLogin: string;
}

interface AdminDashboardProps {
  user: AdminUser;
  onLogout: () => void;
  stats: DashboardStats | null;
  loading: boolean;
  onRefresh: () => void;
  onViewAllEntries: () => void;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({
  user,
  onLogout,
  stats,
  loading,
  onRefresh,
  onViewAllEntries
}) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatWalletAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const getEmailStatusBadge = (emailSent: boolean) => {
    return emailSent ? (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
        Sent
      </span>
    ) : (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
        </svg>
        Pending
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-[#1E2329]">
      {/* Notificaciones - SORPRESA 2 */}
      <AdminNotifications stats={stats} />
      
      {/* Keyboard Shortcuts - SORPRESA 4 */}
      <AdminKeyboardShortcuts 
        onRefresh={onRefresh}
        onViewAllEntries={onViewAllEntries}
        onExport={() => {
          const csvData = stats?.recent_registrations?.map(entry => 
            `${entry.name},${entry.email},${entry.wallet_address},${entry.created_at},${entry.email_sent}`
          ).join('\n');
          const blob = new Blob([`Name,Email,Wallet,Date,EmailSent\n${csvData}`], { type: 'text/csv' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `pgc-dashboard-${new Date().toISOString().split('T')[0]}.csv`;
          a.click();
        }}
        onLogout={onLogout}
      />
      {/* Header */}
      <header className="bg-[#2B3139] shadow-lg border-b border-[#848E9C] border-opacity-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo and Title */}
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-primary-green rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <h1 className="text-xl font-semibold text-gray-900">PGC Admin Dashboard</h1>
                <p className="text-sm text-gray-500">Whitelist Management System</p>
              </div>
            </div>

            {/* User Menu */}
            <div className="flex items-center space-x-4">
              {/* Current Time */}
              <div className="hidden md:block text-sm text-gray-500">
                {currentTime.toLocaleTimeString()}
              </div>

              {/* Refresh Button */}
              <button
                onClick={onRefresh}
                disabled={loading}
                className="p-2 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-green-light rounded-lg transition-colors"
                title="Refresh Data"
              >
                <svg className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                </svg>
              </button>

              {/* User Info - MEJORADO */}
              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">{user.username}</div>
                  <div className="text-xs text-gray-500">{user.email}</div>
                  <div className="text-xs text-primary-green capitalize font-medium">{user.role}</div>
                </div>
                <div className="relative">
                  <div className="w-10 h-10 bg-gradient-to-br from-primary-green to-primary-green-light rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-white text-sm font-bold">
                      {user.username.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white"></div>
                </div>
              </div>

              {/* Logout Button */}
              <button
                onClick={onLogout}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
              >
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
                </svg>
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {loading && !stats ? (
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner size="large" />
          </div>
        ) : (
          <div className="px-4 py-6 sm:px-0">
            {/* Quick Actions Bar - BINANCE STYLE */}
            <div className="bg-gradient-to-r from-[#F0B90B] to-[#FFD700] rounded-lg p-4 mb-6 text-[#1E2329] shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold">Welcome back, {user.username}! 👋</h2>
                  <p className="text-[#1E2329] opacity-80">Last login: {formatDate(user.lastLogin)}</p>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={onViewAllEntries}
                    className="bg-[#1E2329] bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg transition-all duration-200 flex items-center space-x-2 hover:scale-105 transform"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                    </svg>
                    <span className="text-[#F0B90B] font-semibold">Manage All</span>
                  </button>
                  <button
                    onClick={() => {
                      const csvData = stats?.recent_registrations?.map(entry => 
                        `${entry.name},${entry.email},${entry.wallet_address},${entry.created_at},${entry.email_sent}`
                      ).join('\n');
                      const blob = new Blob([`Name,Email,Wallet,Date,EmailSent\n${csvData}`], { type: 'text/csv' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `pgc-recent-${new Date().toISOString().split('T')[0]}.csv`;
                      a.click();
                    }}
                    className="bg-[#1E2329] bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg transition-all duration-200 flex items-center space-x-2 hover:scale-105 transform"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    <span className="text-[#F0B90B] font-semibold">Quick Export</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Advanced Metrics - SORPRESA 3 */}
            <AdminMetrics stats={stats} />

            {/* Database Status */}
            <DatabaseStatus onRetry={onRefresh} />

            {/* Stats Cards - MEJORADAS */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {/* Total Registrations - BINANCE STYLE */}
              <div className="bg-[#2B3139] overflow-hidden shadow-lg rounded-lg border border-[#848E9C] border-opacity-20 hover:shadow-xl hover:border-[#F0B90B] hover:border-opacity-50 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105">
                <div className="p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#1890FF] to-[#0066CC] rounded-lg flex items-center justify-center shadow-lg">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-4 flex-1">
                      <div className="text-3xl font-bold text-[#FFFFFF] mb-1">
                        {stats?.total_registrations || 0}
                      </div>
                      <div className="text-sm text-[#848E9C]">Total Registrations</div>
                      <div className="mt-2 flex items-center text-xs text-[#02C076]">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                        All time
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Today's Registrations - BINANCE STYLE */}
              <div className="bg-[#2B3139] overflow-hidden shadow-lg rounded-lg border border-[#848E9C] border-opacity-20 hover:shadow-xl hover:border-[#F0B90B] hover:border-opacity-50 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105">
                <div className="p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#02C076] to-[#00A86B] rounded-lg flex items-center justify-center shadow-lg">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-4 flex-1">
                      <div className="text-3xl font-bold text-[#FFFFFF] mb-1">
                        {stats?.today_registrations || 0}
                      </div>
                      <div className="text-sm text-[#848E9C]">Today's Registrations</div>
                      <div className="mt-2 flex items-center text-xs text-[#1890FF]">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                        </svg>
                        {new Date().toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Emails Sent - MEJORADA */}
              <div className="bg-white overflow-hidden shadow-lg rounded-lg border border-gray-200 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-4 flex-1">
                      <div className="text-3xl font-bold text-gray-900 mb-1">
                        {stats?.emails_sent || 0}
                      </div>
                      <div className="text-sm text-gray-500">Emails Sent</div>
                      <div className="mt-2 flex items-center text-xs text-purple-600">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Delivered
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Emails Pending - MEJORADA */}
              <div className="bg-white overflow-hidden shadow-lg rounded-lg border border-gray-200 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center shadow-lg">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-4 flex-1">
                      <div className="text-3xl font-bold text-gray-900 mb-1">
                        {stats?.emails_pending || 0}
                      </div>
                      <div className="text-sm text-gray-500">Emails Pending</div>
                      <div className="mt-2 flex items-center text-xs text-orange-600">
                        <svg className="w-3 h-3 mr-1 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                        </svg>
                        Awaiting
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Registrations */}
            <div className="bg-white shadow-lg rounded-lg border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900">Recent Registrations</h3>
                  <button
                    onClick={onViewAllEntries}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-primary-green bg-primary-green bg-opacity-10 hover:bg-opacity-20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-green-light transition-colors"
                  >
                    View All
                    <svg className="ml-2 w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="overflow-hidden">
                {stats?.recent_registrations && stats.recent_registrations.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            User
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Wallet Address
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Email Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Registered
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {stats.recent_registrations.map((entry) => (
                          <tr key={entry.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-gray-900">{entry.name}</div>
                                <div className="text-sm text-gray-500">{entry.email}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center space-x-2">
                                <div className="text-sm font-mono text-[#FFFFFF] bg-[#2B3139] px-3 py-2 rounded-lg border border-[#848E9C] border-opacity-20 flex-1 min-w-0">
                                  <span className="truncate block" title={entry.wallet_address}>
                                    {entry.wallet_address}
                                  </span>
                                </div>
                                <CopyButton text={entry.wallet_address} />
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {getEmailStatusBadge(entry.email_sent)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(entry.created_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No registrations yet</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Whitelist registrations will appear here when users sign up.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;